<template>
	<div class="esg-container">
		<div class="esg-wrapper" v-for="(item, index) in esgList" :key="index">
			<div class="rating-header">
				<div class="rating-value">
					{{ item.rate }}
				</div>
				<div class="indicator-time">
					<p class="indicator">{{ item.orgName }}</p>
					<p class="time">{{ item.date }}</p>
				</div>
			</div>
			<template v-if="item.esgRateRatioVOList">
				<div class="rating-list">
					<p
						:class="{ 'active-rating': rating.rate === item.rate }"
						v-for="rating in item.reverseEsgRateRatioVOList"
						:key="rating.rate"
					>
						{{ rating.rate }}
					</p>
				</div>
				<div v-if="showDistribution" class="rating-distribution">
					<div
						class="distribution-item"
						:class="{ 'active-distribution': rating.rate === item.rate }"
						v-for="rating in item.esgRateRatioVOList"
						:key="rating.rate"
					>
						<div class="distribution-left">{{ rating.rate }}</div>
						<div class="distribution-right">
							<div class="distribution-ratio">{{ rating.rateNumRatio }}%</div>
							<div
								class="distribution-pie"
								:style="getPieBg(`${rating.rateNumRatio}`, rating.rate === item.rate)"
							></div>
						</div>
					</div>
				</div>
			</template>
		</div>
	</div>
</template>

<script setup>
import { computed } from 'vue';
import { useStore } from '@/utils/vue-api';
import { hexToRgb } from '@/utils/api';

const props = defineProps({
	// ESG原始数据列表
	esgData: {
		type: Array,
		default: () => []
	},
	// 评级配置，用于过滤和排序
	ratingConfig: {
		type: Array,
		default: () => []
	},
	// 是否显示评级分布图，默认显示
	showDistribution: {
		type: Boolean,
		default: true
	}
});

const store = useStore();

// 计算属性
const getIrThemeColor = computed(() => store.getters.getIrThemeColor);

// 处理 ESG 数据，保持与历史组件相同的数据处理逻辑
const esgList = computed(() => {
	const rawData = props.esgData || [];
	if (!rawData.length || !props.ratingConfig.length) return [];

	const esgData = rawData.reduce((obj, ele) => {
		ele.date = ele.pubDate.replace(/-/g, '.');
		obj[ele.orgName] = ele;
		return obj;
	}, {});

	const esgList = [];
	for (let { name } of props.ratingConfig) {
		const item = esgData[name];
		if (!item) {
			continue;
		}
		if (item.esgRateRatioVOList) {
			item.esgRateRatioVOList = item.esgRateRatioVOList.map(rating => {
				rating.rateNumRatio = parseFloat(rating.rateNumRatio).toFixed(2);
				return rating;
			});
			item.reverseEsgRateRatioVOList = [].concat(item.esgRateRatioVOList).reverse();
		}
		esgList.push(item);
	}
	return esgList;
});

// 方法
function getPieBg(value, active) {
	const _value = Math.max(parseFloat(value), 1.5) + '%';
	const emphasisColor = active ? '#fff' : getIrThemeColor.value;
	const emphasisColorRGB = hexToRgb(emphasisColor);
	const normalColor = `rgba(${emphasisColorRGB.r}, ${emphasisColorRGB.g}, ${emphasisColorRGB.b}, ${
		active ? 0.2 : 0.1
	})`;
	return {
		background: `conic-gradient(
            ${emphasisColor} 0% ${_value},
            ${normalColor} ${_value} 100%
        )`
	};
}
</script>

<style lang="less" scoped>
@import '@/style/mixins';

.esg-container {
	width: 100%;
}

.esg-wrapper {
	padding: 0.12rem;
	background: var(--ir-theme-color-opacity-1);
	cursor: pointer;

	.rating-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
	}

	.rating-value {
		height: 0.36rem;
		padding: 0 0.08rem;
		border-radius: 0.02rem;
		background: var(--ir-theme-color);

		color: #fff;
		font-family: 'DIN Alternate';
		font-size: 0.24rem;
		font-style: normal;
		font-weight: 700;
		line-height: 0.36rem;
	}

	.indicator {
		color: #1d2129;
		text-align: right;
		font-family: 'PingFang SC';
		font-size: 0.16rem;
		font-style: normal;
		font-weight: 600;
		line-height: 0.24rem;
	}

	.time {
		margin-top: 0.08rem;

		color: #86909c;
		text-align: right;
		font-family: 'PingFang SC';
		font-size: 0.12rem;
		font-style: normal;
		font-weight: 400;
		line-height: 0.16rem;
	}

	.rating-list {
		margin-top: 0.16rem;
		height: 0.24rem;
		padding: 0 0.12rem;
		border: 1px solid var(--ir-theme-color);

		display: flex;
		justify-content: space-between;

		color: var(--ir-theme-color);
		font-family: 'PingFang SC';
		font-size: 0.12rem;
		font-style: normal;
		font-weight: 400;
		line-height: 0.24rem;

		.active-rating {
			padding: 0 0.06rem;
			background: var(--ir-theme-color);
			color: #fff;
		}
	}

	.rating-distribution {
		margin-top: 0.12rem;

		display: grid;
		grid-template-columns: repeat(3, 1fr);
		grid-gap: 0.1rem;

		.distribution-item {
			min-width: 0;
			height: 0.24rem;
			padding: 0 0.08rem;
			border-radius: 0.02rem;
			background: rgba(0, 0, 0, 0.03);
			display: flex;
			align-items: center;
			justify-content: space-between;

			color: #4e5969;
			font-family: 'PingFang SC';
			font-size: 0.12rem;
			font-style: normal;
			font-weight: 400;

			&.active-distribution {
				background: var(--ir-theme-color);
				color: #fff;
			}

			.distribution-right {
				display: flex;
				align-items: center;

				font-family: 'DIN Alternate';
				font-weight: 700;
			}

			.distribution-pie {
				margin-left: 0.08rem;

				width: 0.16rem;
				height: 0.16rem;
				border-radius: 50%;
			}
		}
	}
}

.esg-wrapper + .esg-wrapper {
	margin-top: 0.12rem;
}
</style>
